'use client';
import React, { createContext, useContext, useState, useCallback } from 'react';
import { type EfStore, fetchStoreFeatures } from '@/lib/services/storeService';
import { setImpersonatedStoreId } from '@/hooks/useStoreId';

interface ImpersonationState {
  active: boolean;
  store: EfStore | null;
  /** Map of feature_key → enabled for the impersonated store */
  features: Record<string, boolean>;
  featuresLoading: boolean;
}

interface ImpersonationContextValue {
  impersonation: ImpersonationState;
  startImpersonation: (store: EfStore) => void;
  stopImpersonation: () => void;
  /** Returns the impersonated store_id if active, otherwise null */
  getImpersonatedStoreId: () => string | null;
  /** Returns true if a feature is enabled in the impersonated store */
  isFeatureEnabled: (featureKey: string) => boolean;
}

const ImpersonationContext = createContext<ImpersonationContextValue>({
  impersonation: { active: false, store: null, features: {}, featuresLoading: false },
  startImpersonation: () => {},
  stopImpersonation: () => {},
  getImpersonatedStoreId: () => null,
  isFeatureEnabled: () => true,
});

export const useImpersonation = () => useContext(ImpersonationContext);

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const [impersonation, setImpersonation] = useState<ImpersonationState>({
    active: false,
    store: null,
    features: {},
    featuresLoading: false,
  });

  const startImpersonation = useCallback(async (store: EfStore) => {
    setImpersonatedStoreId(store.id);
    // Immediately mark active so UI redirects, features load in background
    setImpersonation({ active: true, store, features: {}, featuresLoading: true });

    try {
      const featureRows = await fetchStoreFeatures(store.id);
      const map: Record<string, boolean> = {};
      featureRows.forEach((f) => { map[f.feature_key] = f.enabled; });
      setImpersonation((prev) => ({ ...prev, features: map, featuresLoading: false }));
    } catch {
      // If features fail to load, default to all enabled (graceful degradation)
      setImpersonation((prev) => ({ ...prev, features: {}, featuresLoading: false }));
    }
  }, []);

  const stopImpersonation = useCallback(() => {
    setImpersonatedStoreId(null);
    setImpersonation({ active: false, store: null, features: {}, featuresLoading: false });
  }, []);

  const getImpersonatedStoreId = useCallback((): string | null => {
    return impersonation.active && impersonation.store ? impersonation.store.id : null;
  }, [impersonation]);

  const isFeatureEnabled = useCallback((featureKey: string): boolean => {
    if (!impersonation.active) return true;
    // While loading, allow all (don't flash-hide items)
    if (impersonation.featuresLoading) return true;
    // If no features configured at all, allow all
    if (Object.keys(impersonation.features).length === 0) return true;
    // Explicit check — if key not present, default to enabled
    return impersonation.features[featureKey] !== false;
  }, [impersonation]);

  return (
    <ImpersonationContext.Provider
      value={{ impersonation, startImpersonation, stopImpersonation, getImpersonatedStoreId, isFeatureEnabled }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}
